import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { type AnalyticsConfig, analyticsService, defaultSessionReplaySampleRate, extractAttributionTraits, hasAttributionParams, type ScreenViewProperties } from "@medbillai/app/lib/analytics";

// Although "useEffect" will only run the hook once per mount, hot reloading can
// cause us to mount multiple times, so track initialization separately to avoid
// error noise.
let initialized = false;
export function useAnalyticsService() {
  const {
    pathname,
    query,
    isReady
  } = useRouter();
  // Track if we've already captured attribution for this session
  const attributionCapturedRef = useRef(false);

  // Initialize analytics service on mount
  useEffect(() => {
    if (initialized) {
      return;
    }
    initialized = true;
    if (!process.env.NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY || !process.env.NEXT_PUBLIC_RUDDERSTACK_DATA_PLANE_URL) {
      console.error("[Analytics] NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY/_DATA_PLANE_URL missing — analytics disabled");
      return;
    }
    const config: AnalyticsConfig = {
      writeKey: process.env.NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY,
      dataPlaneUrl: process.env.NEXT_PUBLIC_RUDDERSTACK_DATA_PLANE_URL,
      debug: process.env.NEXT_PUBLIC_RUDDERSTACK_DEBUG === "true",
      amplitudeApiKey: process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY,
      sessionReplay: {
        enabled: process.env.NEXT_PUBLIC_SESSION_REPLAY_ENABLED === "true",
        sampleRate: process.env.NEXT_PUBLIC_SESSION_REPLAY_SAMPLE_RATE != null ? parseFloat(process.env.NEXT_PUBLIC_SESSION_REPLAY_SAMPLE_RATE) : defaultSessionReplaySampleRate
      }
    };
    void analyticsService.initialize(config);
  }, []);

  // Track screen view and handle anonymousId and attribution whenever pathname or query changes
  useEffect(() => {
    // The pages router hydrates in two steps: the first render has query = {}
    // with isReady false, then query populates as a new object and re-runs
    // this effect. Tracking that first pass double-fired every entry page
    // view (once with an empty query) and made attribution capture depend on
    // hasAttributionParams({}) being false.
    if (!isReady) {
      return;
    }

    // Set anonymousId if it exists in URL query params
    if (query.anonymous_id && typeof query.anonymous_id === "string") {
      analyticsService.setAnonymousId(query.anonymous_id);
    }

    // Capture attribution parameters if present and not already captured this session
    // Only capture on first page load to avoid overwriting attribution on
    // internal navigation if parameters get corrupted
    if (!attributionCapturedRef.current && hasAttributionParams(query)) {
      const attributionTraits = extractAttributionTraits(query);
      analyticsService.identifyAnonymousUser(attributionTraits);
      attributionCapturedRef.current = true;
    }

    // Track screen view if pathname exists
    if (pathname) {
      analyticsService.trackScreenView(pathname, query as ScreenViewProperties);
    }
  }, [isReady, pathname, query]);
}